import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { attendanceConceptService } from '@/api/domains/attendance';
import { attendanceConceptCatalogService } from '@/api/domains/catalog/attendanceConceptCatalog';
import type {
  AttendanceConceptHandling,
  AttendanceConceptFormula,
} from '@/api/domains/catalog/attendanceConceptCatalog';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import type { ResponseLike } from '@/utils/apiErrorNotification';
import {
  createAttendanceConceptSchema,
  type CreateAttendanceConceptFormData,
} from '@/schemas/attendance/createAttendanceConceptForm';


interface UseCreateAttendanceConceptPanelParams {
  onSaved?: () => void;
}

export interface UseCreateAttendanceConceptPanelResult {
  nameValue: string;
  codeValue: string;
  handlingValue: string;
  calculationFormulaValue: string;
  obtentionFormulaValue: string;
  handlingOptions: { value: string; label: string }[];
  formulaOptions: { value: string; label: string }[];
  isSubmitting: boolean;
  isReady: boolean;
  isReadOnly: boolean;
  errors: ReturnType<typeof useForm<CreateAttendanceConceptFormData>>['formState']['errors'];
  isSubmitted: boolean;
  handleNameChange: (value: string) => void;
  handleCodeChange: (value: string) => void;
  handleHandlingChange: (value: string | string[]) => void;
  handleCalculationFormulaChange: (value: string | string[]) => void;
  handleObtentionFormulaChange: (value: string | string[]) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
}

export function useCreateAttendanceConceptPanel({
  onSaved,
}: UseCreateAttendanceConceptPanelParams): UseCreateAttendanceConceptPanelResult {
  const { t: translate } = useTranslation();
  const { showSuccess, showApiError, showError, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();
  const { canCreate } = usePagePermissions();

  const isReadOnly = !canCreate;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // ── Local field state ────────────────────────────────────────────────────
  const [nameValue, setNameValue] = useState<string>('');
  const [codeValue, setCodeValue] = useState<string>('');
  const [handlingValue, setHandlingValue] = useState<string>('');
  const [calculationFormulaValue, setCalculationFormulaValue] = useState<string>('');
  const [obtentionFormulaValue, setObtentionFormulaValue] = useState<string>('');

  // ── Catalog loading ────────────────────────────────────────────────────
  const catalogErrorHandler = useCallback(
    (response: ResponseLike) => {
      const fallback = translate('attendance:attendanceConcept.messages.catalogLoadError');
      if (response !== null) {
        showApiError(response, fallback);
      } else {
        showError(fallback);
      }
    },
    [showApiError, showError, translate],
  );

  const handlingCatalog = useCatalogLoader<AttendanceConceptHandling>(
    (signal) => attendanceConceptCatalogService.getHandlings(signal),
    (item) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    catalogErrorHandler,
  );

  const formulaCatalog = useCatalogLoader<AttendanceConceptFormula>(
    (signal) => attendanceConceptCatalogService.getFormulas(signal),
    (item) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    catalogErrorHandler,
  );

  const catalogsLoaded = handlingCatalog.isLoaded && formulaCatalog.isLoaded;

  useEffect(() => {
    setLoading(!catalogsLoaded);
  }, [catalogsLoaded, setLoading]);

  // ── React Hook Form ────────────────────────────────────────────────────
  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    register,
    unregister,
    formState: { errors, isSubmitted },
  } = useForm<CreateAttendanceConceptFormData>({
    resolver: zodResolver(createAttendanceConceptSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      code: '',
      handlingId: null,
      calculationFormulaId: null,
      obtentionFormulaId: null,
    },
  });

  // Register fields manually (local-state-driven pattern)
  useEffect(() => {
    register('name');
    register('code');
    register('handlingId');
    register('calculationFormulaId');
    register('obtentionFormulaId');
    return () => {
      unregister('name');
      unregister('code');
      unregister('handlingId');
      unregister('calculationFormulaId');
      unregister('obtentionFormulaId');
    };
  }, [register, unregister]);

  // Sync local state → form values
  useEffect(() => { setValue('name', nameValue); }, [nameValue, setValue]);
  useEffect(() => { setValue('code', codeValue); }, [codeValue, setValue]);
  useEffect(() => {
    setValue('handlingId', handlingValue ? Number(handlingValue) : null);
  }, [handlingValue, setValue]);
  useEffect(() => {
    setValue('calculationFormulaId', calculationFormulaValue ? Number(calculationFormulaValue) : null);
  }, [calculationFormulaValue, setValue]);
  useEffect(() => {
    setValue('obtentionFormulaId', obtentionFormulaValue ? Number(obtentionFormulaValue) : null);
  }, [obtentionFormulaValue, setValue]);

  // ── isReady ────────────────────────────────────────────────────────────
  const isReady = useMemo(
    () =>
      catalogsLoaded &&
      nameValue.trim().length > 0 &&
      codeValue.trim().length > 0 &&
      handlingValue.length > 0 &&
      calculationFormulaValue.length > 0 &&
      obtentionFormulaValue.length > 0,
    [
      catalogsLoaded,
      nameValue,
      codeValue,
      handlingValue,
      calculationFormulaValue,
      obtentionFormulaValue,
    ],
  );

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleNameChange = useCallback((value: string) => {
    setNameValue(value);
  }, []);

  const handleCodeChange = useCallback((value: string) => {
    setCodeValue(value);
  }, []);

  const handleHandlingChange = useCallback((value: string | string[]) => {
    setHandlingValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  const handleCalculationFormulaChange = useCallback((value: string | string[]) => {
    setCalculationFormulaValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  const handleObtentionFormulaChange = useCallback((value: string | string[]) => {
    setObtentionFormulaValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────
  const onValidSubmit = useCallback(
    async (values: CreateAttendanceConceptFormData) => {
      if (isReadOnly) return;
      setIsSubmitting(true);

      try {
        const response = await attendanceConceptService.create({
          name: values.name,
          code: values.code,
          handlingId: values.handlingId as number,
          calculationFormulaId: values.calculationFormulaId as number,
          obtentionFormulaId: values.obtentionFormulaId as number,
        });

        if (!response.success) {
          if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
            injectValidationErrors(response.error.validationErrors, { setError });
            return;
          }
          showApiError(response, translate('attendance:attendanceConcept.messages.createError'));
          return;
        }

        showSuccess(translate('attendance:attendanceConcept.messages.createSuccess'));
        closeSidePanel();
        onSaved?.();
      } finally {
        setIsSubmitting(false);
      }
    },
    [isReadOnly, setError, showApiError, showSuccess, closeSidePanel, onSaved, translate],
  );

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      if (isReadOnly) return;
      void rhfHandleSubmit(onValidSubmit)(event as unknown as React.BaseSyntheticEvent);
    },
    [rhfHandleSubmit, onValidSubmit, isReadOnly],
  );

  return {
    nameValue,
    codeValue,
    handlingValue,
    calculationFormulaValue,
    obtentionFormulaValue,
    handlingOptions: handlingCatalog.options,
    formulaOptions: formulaCatalog.options,
    isSubmitting,
    isReady,
    isReadOnly,
    errors,
    isSubmitted,
    handleNameChange,
    handleCodeChange,
    handleHandlingChange,
    handleCalculationFormulaChange,
    handleObtentionFormulaChange,
    handleSubmit,
  };
}

export default useCreateAttendanceConceptPanel;
